/**
 * SeriesSessionsList — sortable list of sessions attached to a series,
 * with per-row "Day" number input + "Dark mode" toggle.
 *
 * Item shape (built by the parent from `state.sortOrder` +
 * `state.sessionData`):
 *   { id: '12', label: 'Releasing Anxiety', seriesDay: 3,
 *     isDarkMode: false }
 *
 * Each row's controls write back through `onDataChange(id, patch)`
 * with `{ series_day: number, session_is_dark_mode: 0|1 }` — same
 * shape v1's `SortableSeriesList` ships, so the form's submit can
 * stay unchanged.
 *
 * Reorder fires `onSortEnd({ oldIndex, newIndex })` to match v1's
 * arrayMove-on-parent contract.
 */
import React, { useCallback, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

const DragGlyph = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
    <circle cx="9" cy="6" r="0.5" />
    <circle cx="9" cy="12" r="0.5" />
    <circle cx="9" cy="18" r="0.5" />
    <circle cx="15" cy="6" r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

function CompactToggle({ checked, onChange, label }) {
  return (
    <label
      className="inline-flex items-center cursor-pointer select-none"
      style={{ gap: 6, flexShrink: 0 }}
      title={label}
    >
      <span style={{ position: 'relative', width: 30, height: 18, display: 'inline-block' }}>
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onChange}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            background: checked ? '#75A293' : '#E8E0CF',
            transition: 'background 160ms ease',
          }}
        />
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 14 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px #2C3E5040',
            transition: 'left 160ms ease',
          }}
        />
      </span>
      <span
        className="text-text-muted"
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontFamily: 'var(--font-condensed)',
        }}
      >
        {label}
      </span>
    </label>
  );
}

function SortableRow({ item, index, onDataChange }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center">
      <div
        className="flex items-center"
        style={{
          flex: 1,
          gap: 10,
          padding: '8px 12px',
          background: '#FAF7F1',
          border: '1px solid #E8E0CF',
          borderRadius: 8,
          marginBottom: 6,
          minWidth: 0,
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#D6CCAE';
          e.currentTarget.style.boxShadow = '0 1px 3px #2C3E5012';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#E8E0CF';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          className="grid place-items-center"
          style={{
            width: 22,
            height: 22,
            border: 0,
            background: 'transparent',
            color: '#9CA1A8',
            cursor: 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        >
          {DragGlyph}
        </button>

        <span
          className="font-mono text-text-muted"
          style={{ fontSize: 11, minWidth: 22, textAlign: 'right' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        <span
          className="text-navy"
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {item.label}
        </span>

        <CompactToggle
          checked={item.isDarkMode}
          label="Dark"
          onChange={(e) =>
            onDataChange(item.id, {
              series_day: item.seriesDay || 0,
              session_is_dark_mode: e.target.checked ? 1 : 0,
            })
          }
        />

        <div
          className="flex items-center"
          style={{ gap: 4, flexShrink: 0 }}
          title="Day in the series"
        >
          <span
            className="font-condensed font-bold uppercase text-text-muted"
            style={{ fontSize: 10, letterSpacing: '0.08em' }}
          >
            Day
          </span>
          <input
            type="number"
            min={0}
            value={item.seriesDay || ''}
            onChange={(e) =>
              onDataChange(item.id, {
                series_day: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                session_is_dark_mode: item.isDarkMode ? 1 : 0,
              })
            }
            placeholder="0"
            className="font-mono"
            style={{
              width: 64,
              padding: '6px 10px',
              fontSize: 13,
              border: '1px solid #E8E0CF',
              borderRadius: 4,
              background: '#FAF4EA',
              color: '#2C3E50',
              outline: 'none',
              textAlign: 'center',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function SeriesSessionsList({
  items,
  onSortEnd,
  onDataChange,
  emptyText = 'No sessions attached yet.',
}) {
  const ids = useMemo(() => (items || []).map((i) => i.id), [items]);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.findIndex((id) => id === active.id);
      const newIndex = ids.findIndex((id) => id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onSortEnd?.({ oldIndex, newIndex });
    },
    [ids, onSortEnd],
  );

  if (!items || items.length === 0) {
    return (
      <div
        className="text-text-muted"
        style={{
          padding: 16,
          fontSize: 13,
          textAlign: 'center',
          background: '#FAF7F1',
          border: '1px dashed #E8E0CF',
          borderRadius: 8,
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableRow
            key={item.id}
            item={item}
            index={index}
            onDataChange={onDataChange}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
